import React, { useEffect, useState } from 'react';
import './AddRecommendationIntermediary.css';
import InputMask from 'react-input-mask';
import jwtDecode from 'jwt-decode';
import { getCookie } from 'cookies-next';
import { getConsortiumAgencies, newLead } from '../../utils/api';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import Modal from 'react-modal';

const emptyPerson = {
    name: '',
    nationality: '',
    maritalStatus: '',
    profession: '',
    state: '',
    address: '',
    number: '',
    neighborhood: '',
    city: '',
    zipCode: '',
    notaryCity: '',
    cpf: '',
    assignmentValue: '',
};

const emptyCompany = {
    cnpj: '',
    corporateName: '',
    address: '',
    number: '',
    neighborhood: '',
    city: '',
    zipCode: '',
    state: '',
};

const emptyQuota = {
    group: '',
    quota: '',
    contract: '',
    creditValue: '',
    commonFundPercent: '',
    receivableValue: '',
    notaryCity: '',
};

const emptyBank = {
    pix: '',
    bank: '',
    agency: '',
    account: '',
    accountType: '',
    holderName: '',
    holderDocument: '',
    useFavoredDataForPayment: false,
};

const emptyRegistry = {
    notaryCity: '',
    assignmentValue: '',
    signatureRecognition: false,
};

const defaultAdministrators = [
    {
        name: 'Outra',
        cnpj: '',
        address: '',
        number: '',
        neighborhood: '',
        city: '',
        zipCode: '',
        state: '',
    },
];

const normalizeConsortiumAgency = (agency) => ({
    name: agency.name || agency.agency_name || agency.companyName || agency.corporateName || agency.razaoSocial || agency.razao_social || '',
    cnpj: agency.cnpj || agency.document || agency.document_number || '',
    address: agency.address || agency.street || agency.logradouro || '',
    number: agency.number || agency.address_number || agency.numero || '',
    neighborhood: agency.neighborhood || agency.district || agency.bairro || '',
    city: agency.city || agency.localidade || agency.cidade || '',
    zipCode: agency.zipCode || agency.zipcode || agency.zip_code || agency.cep || '',
    state: agency.state || agency.uf || agency.estado || '',
});

const brazilianStates = [
    'AC',
    'AL',
    'AP',
    'AM',
    'BA',
    'CE',
    'DF',
    'ES',
    'GO',
    'MA',
    'MT',
    'MS',
    'MG',
    'PA',
    'PB',
    'PR',
    'PE',
    'PI',
    'RJ',
    'RN',
    'RS',
    'RO',
    'RR',
    'SC',
    'SP',
    'SE',
    'TO',
];

const initialForm = {
    assignorType: 'cpf',
    assignorPerson: { ...emptyPerson },
    assignorCompany: { ...emptyCompany },
    representative: { ...emptyPerson },
    administrator: { ...emptyCompany, name: '' },
    quotas: [{ ...emptyQuota }],
    bank: { ...emptyBank },
    registry: { ...emptyRegistry },
};

const steps = [
    'Cedente',
    'Dados bancários',
    'Administradora',
    'Dados da cota',
    'Cartório e cessão',
];

const parseCurrency = (value) => {
    if (!value) {
        return 0;
    }

    return Number(value.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
};

const parsePercent = (value) => {
    if (!value) {
        return 0;
    }

    return Number(value.replace(',', '.')) || 0;
};

const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });
};

function AddRecommendationIntermediary({ isOpen, closeModal }) {
    const [form, setForm] = useState(initialForm);
    const [currentStep, setCurrentStep] = useState(0);
    const [administrators, setAdministrators] = useState(defaultAdministrators);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

    Modal.setAppElement('#root');

    useEffect(() => {
        async function loadConsortiumAgencies() {
            try {
                const response = await getConsortiumAgencies();
                const agencies = Array.isArray(response) ? response : response?.data || [];
                const normalizedAgencies = agencies
                    .map(normalizeConsortiumAgency)
                    .filter((agency) => agency.name);

                setAdministrators([
                    ...normalizedAgencies,
                    ...defaultAdministrators,
                ]);
            } catch (error) {
                setAdministrators(defaultAdministrators);
            }
        }

        if (isOpen) {
            loadConsortiumAgencies();
        }
    }, [isOpen]);

    const updateForm = (field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    };

    const updateSection = (section, field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [section]: {
                ...currentForm[section],
                [field]: value,
            },
        }));
    };

    const updateQuota = (index, field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: currentForm.quotas.map((quota, quotaIndex) => {
                if (quotaIndex !== index) {
                    return quota;
                }

                const updatedQuota = { ...quota, [field]: value };

                if (field === 'creditValue' || field === 'commonFundPercent') {
                    const creditValue = parseCurrency(updatedQuota.creditValue);
                    const percentValue = parsePercent(updatedQuota.commonFundPercent);

                    updatedQuota.receivableValue = creditValue && percentValue
                        ? formatCurrency((creditValue * percentValue) / 100)
                        : '';
                }

                return updatedQuota;
            }),
        }));
    };

    const fillAddressByZipCode = async (section, zipCode) => {
        const cleanZipCode = zipCode.replace(/\D/g, '');

        if (cleanZipCode.length !== 8) {
            return;
        }

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanZipCode}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast.warning('CEP nao encontrado.');
                return;
            }

            setForm((currentForm) => ({
                ...currentForm,
                [section]: {
                    ...currentForm[section],
                    address: data.logradouro || currentForm[section].address,
                    neighborhood: data.bairro || currentForm[section].neighborhood,
                    city: data.localidade || currentForm[section].city,
                    state: data.uf || currentForm[section].state,
                },
            }));
        } catch (error) {
            toast.warning('Nao foi possivel buscar o CEP.');
        }
    };

    const selectAdministrator = (name) => {
        const selectedAdministrator = administrators.find((administrator) => administrator.name === name);

        setForm((currentForm) => ({
            ...currentForm,
            administrator: {
                ...currentForm.administrator,
                ...(selectedAdministrator || {}),
                name,
            },
        }));
    };

    const toggleUseFavoredDataForPayment = (checked) => {
        setForm((currentForm) => {
            if (!checked) {
                return {
                    ...currentForm,
                    bank: {
                        ...currentForm.bank,
                        useFavoredDataForPayment: false,
                    },
                };
            }

            const isCpfAssignor = currentForm.assignorType === 'cpf';
            const favoredName = isCpfAssignor
                ? currentForm.assignorPerson.name
                : currentForm.assignorCompany.corporateName;

            return {
                ...currentForm,
                    bank: {
                        ...currentForm.bank,
                        useFavoredDataForPayment: true,
                        holderName: favoredName,
                        holderDocument: isCpfAssignor ? currentForm.assignorPerson.cpf : currentForm.assignorCompany.cnpj,
                    },
            };
        });
    };

    const addQuota = () => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: [...currentForm.quotas, { ...emptyQuota }],
        }));
    };

    const removeQuota = (index) => {
        setForm((currentForm) => ({
            ...currentForm,
            quotas: currentForm.quotas.filter((_, quotaIndex) => quotaIndex !== index),
        }));
    };

    const clearInput = () => {
        setForm(initialForm);
        setCurrentStep(0);
    };

    const requestClose = () => {
        setConfirmCloseOpen(true);
    };

    const cancelClose = () => {
        setConfirmCloseOpen(false);
    };

    const handleClose = () => {
        clearInput();
        setConfirmCloseOpen(false);
        closeModal();
    };

    const validateCedenteStep = () => {
        if (form.assignorType === 'cpf' && !form.assignorPerson.name) {
            toast.warning('Informe o nome do cedente.');
            return false;
        }

        if (form.assignorType === 'cnpj' && !form.assignorCompany.cnpj) {
            toast.warning('Informe o CNPJ do cedente.');
            return false;
        }

        return true;
    };

    const validateBankStep = () => {
        if (!form.bank.bank) {
            toast.warning('Informe o banco.');
            return false;
        }

        return true;
    };

    const validateAdministratorStep = () => {
        if (!form.administrator.name) {
            toast.warning('Informe o nome da administradora.');
            return false;
        }

        return true;
    };

    const validateQuotaStep = () => {
        if (!form.quotas[0]?.group) {
            toast.warning('Informe ao menos um grupo da cota.');
            return false;
        }

        return true;
    };

    const validateRegistryStep = () => {
        if (!form.registry.notaryCity) {
            toast.warning('Informe a cidade do cartorio.');
            return false;
        }

        return true;
    };

    const validateCurrentStep = () => {
        const validators = [
            validateCedenteStep,
            validateBankStep,
            validateAdministratorStep,
            validateQuotaStep,
            validateRegistryStep,
        ];

        return validators[currentStep]();
    };

    const goToNextStep = () => {
        if (!validateCurrentStep()) {
            return;
        }

        setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
    };

    const goToPreviousStep = () => {
        setCurrentStep((step) => Math.max(step - 1, 0));
    };

    const buildPayload = () => {
        const firstQuota = form.quotas[0] || emptyQuota;
        const assignorName = form.assignorType === 'cpf'
            ? form.assignorPerson.name
            : form.assignorCompany.corporateName;

        return {
            name: assignorName,
            status: 'AGUARDANDO',
            creditValue: firstQuota.creditValue,
            percentValue: firstQuota.commonFundPercent,
            intermediaryRecommendation: form,
        };
    };

    async function addRecommendation() {
        if (!validateCurrentStep()) {
            return false;
        }

        const token = getCookie('token');
        const decodedToken = jwtDecode(token);
        const payload = buildPayload();
        const promise = newLead(payload, decodedToken.referrerUser.id);

        toast.promise(
            promise,
            {
                pending: 'Aguarde...',
                success: 'Cadastro realizado com sucesso!',
                error: 'Erro ao cadastrar',
            }
        );

        const response = await promise;

        if (response) {
            clearInput();
            closeModal();
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={requestClose}
            contentLabel="Cadastro de indicacao"
            className="add-intermediary-modal"
            shouldCloseOnOverlayClick={true}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000,
                },
                content: {
                    zIndex: 1001,
                },
            }}
        >
            <div className="add-intermediary-header">
                <h1>Nova Intermediação</h1>
                <button type="button" className="add-intermediary-close" onClick={requestClose}>X</button>
            </div>

            <div className="add-intermediary-steps">
                {steps.map((step, index) => (
                    <button
                        type="button"
                        key={step}
                        className={`add-intermediary-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'done' : ''}`}
                        onClick={() => setCurrentStep(index)}
                    >
                        <span>{index + 1}</span>
                        {step}
                    </button>
                ))}
            </div>

            <div className="add-intermediary-body">
                {currentStep === 0 && (
                    <section className="add-intermediary-section">
                        <div className="add-intermediary-section-title">
                            <h2>Cedente</h2>
                            <div className="add-intermediary-segmented">
                                <button
                                    type="button"
                                    className={form.assignorType === 'cpf' ? 'active' : ''}
                                    onClick={() => updateForm('assignorType', 'cpf')}
                                >
                                    CPF
                                </button>
                                <button
                                    type="button"
                                    className={form.assignorType === 'cnpj' ? 'active' : ''}
                                    onClick={() => updateForm('assignorType', 'cnpj')}
                                >
                                    CNPJ
                                </button>
                            </div>
                        </div>

                        {form.assignorType === 'cpf' ? (
                            <PersonFields
                                values={form.assignorPerson}
                                onChange={(field, value) => updateSection('assignorPerson', field, value)}
                                onZipCodeBlur={(zipCode) => fillAddressByZipCode('assignorPerson', zipCode)}
                                nameLabel="Nome"
                                cityLabel="Cidade"
                                addressTitle="Endereço"
                            />
                        ) : (
                            <>
                                <CompanyFields
                                    values={form.assignorCompany}
                                    onChange={(field, value) => updateSection('assignorCompany', field, value)}
                                    onZipCodeBlur={(zipCode) => fillAddressByZipCode('assignorCompany', zipCode)}
                                    includeName={false}
                                    addressTitle="Dados de endereço"
                                />

                                <div className="add-intermediary-subtitle">Representante</div>
                                <PersonFields
                                    values={form.representative}
                                    onChange={(field, value) => updateSection('representative', field, value)}
                                    onZipCodeBlur={(zipCode) => fillAddressByZipCode('representative', zipCode)}
                                    nameLabel="Nome"
                                    cityLabel="Cidade"
                                    addressTitle="Endereço (Representante)"
                                />
                            </>
                        )}
                    </section>
                )}

                {currentStep === 1 && (
                    <section className="add-intermediary-section">
                        <h2>Dados bancários</h2>
                        <CheckboxInput
                            label="Utilizar dados do Favorecido para o pagamento"
                            checked={form.bank.useFavoredDataForPayment}
                            onChange={toggleUseFavoredDataForPayment}
                            variant="subtitle"
                        />
                        <div className="add-intermediary-grid add-intermediary-bank-grid">
                            <TextInput label="Banco" value={form.bank.bank} onChange={(value) => updateSection('bank', 'bank', value)} />
                            <TextInput label="Agência" value={form.bank.agency} onChange={(value) => updateSection('bank', 'agency', value)} />
                            <TextInput label="Conta" value={form.bank.account} onChange={(value) => updateSection('bank', 'account', value)} />
                            <SelectInput label="Tipo de Conta" value={form.bank.accountType} onChange={(value) => updateSection('bank', 'accountType', value)} options={['Corrente', 'Poupanca', 'Pagamento']} />
                            <TextInput label="Nome titular" value={form.bank.holderName} onChange={(value) => updateSection('bank', 'holderName', value)} wide={true} />
                            <TextInput label="Documento" value={form.bank.holderDocument} onChange={(value) => updateSection('bank', 'holderDocument', value)} />
                            <TextInput label="Pix" value={form.bank.pix} onChange={(value) => updateSection('bank', 'pix', value)} />
                        </div>
                    </section>
                )}

                {currentStep === 2 && (
                    <section className="add-intermediary-section">
                        <h2>Administradora</h2>
                        <AdministratorFields
                            administrators={administrators}
                            values={form.administrator}
                            onChange={(field, value) => updateSection('administrator', field, value)}
                            onSelectAdministrator={selectAdministrator}
                            onZipCodeBlur={(zipCode) => fillAddressByZipCode('administrator', zipCode)}
                        />
                    </section>
                )}

                {currentStep === 3 && (
                    <section className="add-intermediary-section">
                        <div className="add-intermediary-section-title">
                            <h2>Dados da cota</h2>
                            <button type="button" className="add-intermediary-small-btn" onClick={addQuota}>Adicionar cota</button>
                        </div>

                        {form.quotas.map((quota, index) => (
                            <div className="add-intermediary-quota" key={`quota-${index}`}>
                                <div className="add-intermediary-quota-title">
                                    <span>Cota {index + 1}</span>
                                    {form.quotas.length > 1 && (
                                        <button type="button" className="add-intermediary-remove-btn" onClick={() => removeQuota(index)}>Remover</button>
                                    )}
                                </div>
                                <div className="add-intermediary-quota-grid">
                                    <TextInput label="Grupo" value={quota.group} onChange={(value) => updateQuota(index, 'group', value)} />
                                    <TextInput label="Cota" value={quota.quota} onChange={(value) => updateQuota(index, 'quota', value)} />
                                    <TextInput label="Contrato" value={quota.contract} onChange={(value) => updateQuota(index, 'contract', value)} />
                                    <MoneyInput label="Valor crédito" value={quota.creditValue} onChange={(value) => updateQuota(index, 'creditValue', value)} />
                                    <MaskedInput label="% fundo comum" mask="99,9999" value={quota.commonFundPercent} onChange={(value) => updateQuota(index, 'commonFundPercent', value)} />
                                    <TextInput label="Valor recebível" value={quota.receivableValue} onChange={() => { }} readOnly={true} />
                                </div>
                            </div>
                        ))}
                    </section>
                )}

                {currentStep === 4 && (
                    <section className="add-intermediary-section">
                        <h2>Cartório e cessão</h2>
                        <div className="add-intermediary-grid">
                            <TextInput label="Cidade (cartório)" value={form.registry.notaryCity} onChange={(value) => updateSection('registry', 'notaryCity', value)} />
                            <MoneyInput label="Valor da cessão" value={form.registry.assignmentValue} onChange={(value) => updateSection('registry', 'assignmentValue', value)} />
                            <CheckboxInput
                                label="Reconhecimento de firma"
                                checked={form.registry.signatureRecognition}
                                onChange={(value) => updateSection('registry', 'signatureRecognition', value)}
                            />
                        </div>
                    </section>
                )}
            </div>

            <div className="add-intermediary-footer">
                
                {currentStep > 0 && (
                    <button type="button" className="add-intermediary-back" onClick={goToPreviousStep}>Voltar</button>
                )}
                {currentStep < steps.length - 1 ? (
                    <button type="button" className="add-intermediary-submit" onClick={goToNextStep}>Proximo</button>
                ) : (
                    <button type="button" className="add-intermediary-submit" onClick={addRecommendation}>Indicar</button>
                )}
            </div>
            {confirmCloseOpen && (
                <div className="add-intermediary-confirm-overlay">
                    <div className="add-intermediary-confirm-modal">
                        <h3>Descartar dados?</h3>
                        <p>Se fechar agora, os dados preenchidos serao perdidos.</p>
                        <div className="add-intermediary-confirm-actions">
                            <button type="button" className="add-intermediary-back" onClick={cancelClose}>Continuar preenchendo</button>
                            <button type="button" className="add-intermediary-danger" onClick={handleClose}>Descartar</button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function PersonFields({ values, onChange, onZipCodeBlur, nameLabel, cityLabel, addressTitle = 'Dados de endereço' }) {
    return (
        <div className="add-intermediary-grid add-intermediary-person-grid">
            <TextInput label={nameLabel} value={values.name} onChange={(value) => onChange('name', value)} wide={true} />
            <MaskedInput label="CPF" mask="999.999.999-99" value={values.cpf} onChange={(value) => onChange('cpf', value)} />
            <TextInput label="Nacionalidade" value={values.nationality} onChange={(value) => onChange('nationality', value)} />
            <TextInput label="Estado Civil" value={values.maritalStatus} onChange={(value) => onChange('maritalStatus', value)} />
            <TextInput label="Profissão" value={values.profession} onChange={(value) => onChange('profession', value)} />
            <div className="add-intermediary-address-title">{addressTitle}</div>
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label={cityLabel} value={values.city} onChange={(value) => onChange('city', value)} />
            <SelectInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} options={brazilianStates} />
        </div>
    );
}

function CompanyFields({ values, onChange, onZipCodeBlur, includeName, nameLabel, addressTitle = 'Dados de endereço' }) {
    return (
        <div className="add-intermediary-grid add-intermediary-company-grid">
            {includeName && (
                <TextInput label={nameLabel} value={values.name} onChange={(value) => onChange('name', value)} />
            )}
            <MaskedInput label="CNPJ" mask="99.999.999/9999-99" value={values.cnpj} onChange={(value) => onChange('cnpj', value)} />
            {!includeName && (
                <TextInput label="Razão Social" value={values.corporateName} onChange={(value) => onChange('corporateName', value)} />
            )}
            <div className="add-intermediary-address-title">{addressTitle}</div>
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label="Cidade" value={values.city} onChange={(value) => onChange('city', value)} />
            <SelectInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} options={brazilianStates} />
        </div>
    );
}

function AdministratorFields({ administrators, values, onChange, onSelectAdministrator, onZipCodeBlur }) {
    return (
        <div className="add-intermediary-grid add-intermediary-administrator-grid" onKeyDown={(event) => event.target.tagName === 'INPUT' && event.preventDefault()}>
            <SelectInput
                label="Nome administradora"
                value={values.name}
                onChange={onSelectAdministrator}
                options={administrators.map((administrator) => administrator.name)}
            />
            <MaskedInput label="CNPJ" mask="99.999.999/9999-99" value={values.cnpj} onChange={(value) => onChange('cnpj', value)} readOnly={true} />
            <MaskedInput label="CEP" mask="99999-999" value={values.zipCode} onChange={(value) => onChange('zipCode', value)} onBlur={() => onZipCodeBlur(values.zipCode)} />
            <TextInput label="Rua" value={values.address} onChange={(value) => onChange('address', value)} wide={true} />
            <TextInput label="Número" value={values.number} onChange={(value) => onChange('number', value)} />
            <TextInput label="Bairro" value={values.neighborhood} onChange={(value) => onChange('neighborhood', value)} />
            <TextInput label="Cidade" value={values.city} onChange={(value) => onChange('city', value)} />
            <TextInput label="Estado" value={values.state} onChange={(value) => onChange('state', value)} readOnly={true} />
        </div>
    );
}

function TextInput({ label, value, onChange, readOnly = false, wide = false }) {
    return (
        <label className={`add-intermediary-field ${wide ? 'add-intermediary-field-wide' : ''}`}>
            <span>{label}</span>
            <input type="text" value={value} onChange={(event) => onChange(event.target.value)} readOnly={readOnly} />
        </label>
    );
}

function MaskedInput({ label, value, onChange, onBlur, mask, readOnly = false }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <InputMask mask={mask} maskChar={null} value={value} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} readOnly={readOnly} />
        </label>
    );
}

function MoneyInput({ label, value, onChange }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <NumericFormat
                value={value}
                onValueChange={({ formattedValue }) => onChange(formattedValue)}
                decimalSeparator=","
                thousandSeparator="."
                prefix="R$ "
                fixedDecimalScale={true}
                decimalScale={2}
                allowNegative={false}
            />
        </label>
    );
}

function SelectInput({ label, value, onChange, options }) {
    return (
        <label className="add-intermediary-field">
            <span>{label}</span>
            <select value={value} onChange={(event) => onChange(event.target.value)}>
                <option value="">Selecione</option>
                {options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        </label>
    );
}

function CheckboxInput({ label, checked, onChange, variant = '' }) {
    return (
        <label className={`add-intermediary-checkbox ${variant === 'subtitle' ? 'add-intermediary-checkbox-subtitle' : ''}`}>
            <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
            <span>{label}</span>
        </label>
    );
}

export default AddRecommendationIntermediary;
